import FormattedMessage from 'I18n/components/FormattedMessage';
import { PageSection, SectionHeader, SectionSubTitle, SectionTitle } from 'pricing-pages-ui-components/semantic/PageSection';
import { useScopedTranslations } from 'pricing-pages-ui-components/i18n/useScopedTranslations';
import { useCallback, useContext } from 'react';
import { useTrackSectionViewed } from '../../hooks/useTrackSectionViewed';
import { getFullUrl } from 'hubspot-url-utils';
import LinkWrapper from 'pricing-pages-ui-components/components/LinkWrapper';
import { getPaymentsProductUrl } from '../../utils/publicKbUrls';
import { PublicI18nContext } from '../../context/I18nContext';
import { useCurrency } from '../../context/CurrencyContext';
import { TRACKING_SOURCES, trackClickedLink } from '../../utils/tracking';
import { isHubSpotSupportedCountry } from './paymentOptionsUtils';
import { HubSpotAdditionalFees, HubSpotCardCTA, HubSpotCardFooter, HubSpotCardHeader, HubSpotPaymentCard, HubSpotPaymentMethodsSection, HubSpotTransactionFees, HubSpotValueProps } from './elements/HubSpotCard';
import { StripeAdditionalFees, StripeCardCTA, StripeCardFooter, StripeCardHeader, StripePaymentCard, StripePaymentMethodsSection, StripeTransactionFees, StripeValueProps } from './elements/StripeCard';
import { ComparisonGrid, ContentCell, DividerCell, HubSpotCardBg, HubSpotIntroCell, MobileCardsLayout, StripeCardBg, StripeIntroCell } from './NewPublicPaymentOptionsSectionStyles';
import paymentOptionsTranslations from 'i2l?query=sporks!../../../lang/payment-options-new/en.lyaml';

/* Both HubSpot Payments and Stripe CTAs land on the same zero-state page,
 * which then branches based on the user's processor selection. */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const REVENUE_ZERO_STATE_PATH = '/l/revenue/zero-state';
export const NewPublicPaymentOptionsSection = ({
  screen
}) => {
  const {
    i18nLoader
  } = useContext(PublicI18nContext);
  const T = useScopedTranslations(paymentOptionsTranslations, i18nLoader);
  const {
    countryCode
  } = useCurrency();
  const hubSpotSupported = isHubSpotSupportedCountry(countryCode);
  const sectionRef = useTrackSectionViewed(TRACKING_SOURCES.PAYMENTS_PROCESSORS_SECTION, screen);
  const handlePaymentCTAClick = useCallback(name => {
    const url = `${getFullUrl('app')}${REVENUE_ZERO_STATE_PATH}`;
    trackClickedLink({
      type: 'internal',
      url,
      name,
      screen,
      trackingSource: TRACKING_SOURCES.PAYMENTS_PROCESSORS_SECTION
    });
    window.location.href = url;
  }, [screen]);
  const handleEnrollClick = useCallback(() => handlePaymentCTAClick('Enroll in payments'), [handlePaymentCTAClick]);
  const handleConnectClick = useCallback(() => handlePaymentCTAClick('Connect with Stripe'), [handlePaymentCTAClick]);
  return /*#__PURE__*/_jsx("div", {
    ref: sectionRef,
    children: /*#__PURE__*/_jsxs(PageSection, {
      children: [/*#__PURE__*/_jsx(SectionHeader, {
        children: /*#__PURE__*/_jsxs(T.Skeleton, {
          widths: ['60%', '80%'],
          heights: [28, 16],
          children: [/*#__PURE__*/_jsx(SectionTitle, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "payment-options-new.title"
            })
          }), /*#__PURE__*/_jsxs(SectionSubTitle, {
            children: [/*#__PURE__*/_jsx(FormattedMessage, {
              message: "payment-options-new.subtitle"
            }), ' ', /*#__PURE__*/_jsx(LinkWrapper, {
              href: getPaymentsProductUrl(),
              external: true,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "payment-options-new.learnMoreLink"
              })
            })]
          })]
        })
      }), /*#__PURE__*/_jsxs(T.Skeleton, {
        widths: ['100%'],
        heights: [520],
        children: [/*#__PURE__*/_jsxs(ComparisonGrid, {
          children: [/*#__PURE__*/_jsx(HubSpotCardBg, {
            isHighlighted: hubSpotSupported
          }), /*#__PURE__*/_jsx(StripeCardBg, {}), /*#__PURE__*/_jsx(HubSpotIntroCell, {
            row: 1,
            col: 1,
            isHighlighted: hubSpotSupported,
            children: /*#__PURE__*/_jsx(HubSpotCardHeader, {
              isRecommended: hubSpotSupported
            })
          }), /*#__PURE__*/_jsx(StripeIntroCell, {
            row: 1,
            col: 2,
            children: /*#__PURE__*/_jsx(StripeCardHeader, {})
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 2,
            col: 1,
            children: /*#__PURE__*/_jsx(HubSpotValueProps, {})
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 2,
            col: 2,
            children: /*#__PURE__*/_jsx(StripeValueProps, {})
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 3,
            col: 1,
            children: /*#__PURE__*/_jsx(HubSpotCardCTA, {
              onEnrollClick: handleEnrollClick
            })
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 3,
            col: 2,
            children: /*#__PURE__*/_jsx(StripeCardCTA, {
              onConnectClick: handleConnectClick
            })
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 4,
            col: 1
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 4,
            col: 2
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 5,
            col: 1,
            children: /*#__PURE__*/_jsx(HubSpotTransactionFees, {
              countryCode: countryCode
            })
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 5,
            col: 2,
            children: /*#__PURE__*/_jsx(StripeTransactionFees, {
              countryCode: countryCode
            })
          }), countryCode !== 'GB' && countryCode !== 'CA' && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(ContentCell, {
              row: 6,
              col: 1,
              children: /*#__PURE__*/_jsx(HubSpotAdditionalFees, {})
            }), /*#__PURE__*/_jsx(ContentCell, {
              row: 6,
              col: 2,
              children: /*#__PURE__*/_jsx(StripeAdditionalFees, {})
            })]
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 7,
            col: 1
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 7,
            col: 2
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 8,
            col: 1,
            children: /*#__PURE__*/_jsx(HubSpotPaymentMethodsSection, {
              countryCode: countryCode
            })
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 8,
            col: 2,
            children: /*#__PURE__*/_jsx(StripePaymentMethodsSection, {
              countryCode: countryCode
            })
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 9,
            col: 1
          }), /*#__PURE__*/_jsx(DividerCell, {
            row: 9,
            col: 2
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 10,
            col: 1,
            children: /*#__PURE__*/_jsx(HubSpotCardFooter, {})
          }), /*#__PURE__*/_jsx(ContentCell, {
            row: 10,
            col: 2,
            children: /*#__PURE__*/_jsx(StripeCardFooter, {})
          })]
        }), /*#__PURE__*/_jsxs(MobileCardsLayout, {
          children: [/*#__PURE__*/_jsx(HubSpotPaymentCard, {
            onEnrollClick: handleEnrollClick,
            countryCode: countryCode,
            isRecommended: hubSpotSupported
          }), /*#__PURE__*/_jsx(StripePaymentCard, {
            onConnectClick: handleConnectClick,
            countryCode: countryCode
          })]
        })]
      })]
    })
  });
};